import { Price } from './fractions/price'
import { TokenAmount } from './fractions/tokenAmount'
import invariant from 'tiny-invariant'
import JSBI from 'jsbi'
import { pack, keccak256 } from '@ethersproject/solidity'
import { getCreate2Address } from '@ethersproject/address'

import {
  BigintIsh,
  MINIMUM_LIQUIDITY,
  ZERO,
  ONE,
  FIVE,
  _997,
  _1000,
  ChainId,
  ROUTER_ADDRESS_WITH_PRICE
} from '../constants'
import {
  sqrt,
  parseBigintIsh,
  isContractWithPrice,
  solidityPack
} from '../utils'
import {
  InsufficientReservesError,
  InsufficientInputAmountError
} from '../errors'
import { Token } from './token'
import Web3 from 'web3'
import { PYTH_ADDRESS, RPC_URLS } from '../../constants/constants'
import IRouter from '../../abis/IBrownFiV2Router.json'
import IRouterWithPrice from '../../abis/IRouterWithPrice.json'
import IPair from '../../abis/IPair.json'
import IPairV2 from '../../abis/IPairV2.json'
import IPythPriceFeed from '../../abis/IPythPriceFeed.json'
import IPyth from '../../abis/IPyth.json'
import axios from 'axios'
import { Buffer } from 'buffer'
import {
  getFactoryAddress,
  getInitCodeHash,
  getRouterAddress
} from '../../sdk/utils'

let PAIR_ADDRESS_CACHE: {
  [token0Address: string]: { [token1Address: string]: string }
} = {}

export const ZERO_ADDRESS =
  '0x0000000000000000000000000000000000000000000000000000000000000000'

async function getFeedPriceUpdateData(ids: string[]) {
  ids = ids.filter((id) => id !== ZERO_ADDRESS)
  const response = await axios.get(
    'https://hermes.pyth.network/api/latest_vaas',
    { params: { ids } }
  )
  return response.data.map(
    (vaa: any) => '0x' + Buffer.from(vaa, 'base64').toString('hex')
  )
}

/**
 * Use when Swap get amountOuts / amountIns v1 - Berachain
 */
async function getFeedPriceAndFee(
  pairs: Pair[],
  chainId: ChainId
): Promise<[string[], number]> {
  const web3 = new Web3(new Web3.providers.HttpProvider(RPC_URLS[chainId]))

  const pythContract = new web3.eth.Contract(
    IPyth as any,
    PYTH_ADDRESS[chainId]
  )

  const allIds = await Promise.all(
    pairs.map(async (pair) => {
      const pairContract = new web3.eth.Contract(
        IPair as any,
        pair.liquidityToken.address
      )
      const priceFeedAddress = await pairContract.methods.priceFeed().call()

      const pythPriceFeedContract = new web3.eth.Contract(
        IPythPriceFeed as any,
        priceFeedAddress
      )

      const [baseTokenPriceId, quoteTokenPriceId]: [string, string] =
        await Promise.all([
          pythPriceFeedContract.methods.baseTokenPriceId().call(),
          pythPriceFeedContract.methods.quoteTokenPriceId().call()
        ])

      return [baseTokenPriceId, quoteTokenPriceId]
    })
  ).then((allIds) => allIds.flat())

  const uniqueIds = allIds.filter(
    (item, index, array) => array.indexOf(item) === index
  )
  const priceUpdate: string[] = await getFeedPriceUpdateData(uniqueIds)

  const updateFee: number = await pythContract.methods
    .getUpdateFee(priceUpdate)
    .call()

  return [priceUpdate, updateFee]
}

export class Pair {
  public readonly liquidityToken: Token
  public readonly tokenAmounts: [TokenAmount, TokenAmount]
  public readonly version: number

  public static getAddress(
    tokenA: Token,
    tokenB: Token,
    version: number
  ): string {
    const tokens = tokenA.sortsBefore(tokenB)
      ? [tokenA, tokenB]
      : [tokenB, tokenA] // does safety checks

    if (
      PAIR_ADDRESS_CACHE?.[tokens[0].address]?.[tokens[1].address] === undefined
    ) {
      PAIR_ADDRESS_CACHE = {
        ...PAIR_ADDRESS_CACHE,
        [tokens[0].address]: {
          ...PAIR_ADDRESS_CACHE?.[tokens[0].address],
          [tokens[1].address]: getCreate2Address(
            getFactoryAddress(tokenA.chainId, version),
            keccak256(
              ['bytes'],
              [
                pack(
                  ['address', 'address'],
                  [tokens[0].address, tokens[1].address]
                )
              ]
            ),
            getInitCodeHash(tokenA.chainId, version)
          )
        }
      }
    }

    return PAIR_ADDRESS_CACHE[tokens[0].address][tokens[1].address]
  }

  public constructor(
    tokenAmountA: TokenAmount,
    tokenAmountB: TokenAmount,
    version: number
  ) {
    const tokenAmounts = tokenAmountA.token.sortsBefore(tokenAmountB.token) // does safety checks
      ? [tokenAmountA, tokenAmountB]
      : [tokenAmountB, tokenAmountA]

    const symbol = version === 2 ? `BF-V2` : `BRF-V1`
    const name = version === 2 ? `BrownFi V2` : `BrownFi V1`
    this.liquidityToken = new Token(
      tokenAmounts[0].token.chainId,
      Pair.getAddress(tokenAmounts[0].token, tokenAmounts[1].token, version),
      18,
      symbol,
      name
    )
    this.tokenAmounts = tokenAmounts as [TokenAmount, TokenAmount]
    this.version = version
  }

  /**
   * Returns true if the token is either token0 or token1
   * @param token to check
   */
  public involvesToken(token: Token): boolean {
    return token.equals(this.token0) || token.equals(this.token1)
  }

  /**
   * Returns the current mid price of the pair in terms of token0, i.e. the ratio of reserve1 to reserve0
   */
  public get token0Price(): Price {
    return new Price(
      this.token0,
      this.token1,
      this.tokenAmounts[0].raw,
      this.tokenAmounts[1].raw
    )
  }

  /**
   * Returns the current mid price of the pair in terms of token1, i.e. the ratio of reserve0 to reserve1
   */
  public get token1Price(): Price {
    return new Price(
      this.token1,
      this.token0,
      this.tokenAmounts[1].raw,
      this.tokenAmounts[0].raw
    )
  }

  /**
   * Return the price of the given token in terms of the other token in the pair.
   * @param token token to return price of
   */
  public priceOf(token: Token): Price {
    invariant(this.involvesToken(token), 'TOKEN')
    return token.equals(this.token0) ? this.token0Price : this.token1Price
  }

  /**
   * Returns the chain ID of the tokens in the pair.
   */
  public get chainId(): ChainId {
    return this.token0.chainId
  }

  public get token0(): Token {
    return this.tokenAmounts[0].token
  }

  public get token1(): Token {
    return this.tokenAmounts[1].token
  }

  public get reserve0(): TokenAmount {
    return this.tokenAmounts[0]
  }

  public get reserve1(): TokenAmount {
    return this.tokenAmounts[1]
  }

  public reserveOf(token: Token): TokenAmount {
    invariant(this.involvesToken(token), 'TOKEN')
    return token.equals(this.token0) ? this.reserve0 : this.reserve1
  }

  public async getTradingFee(): Promise<number> {
    const web3 = new Web3(
      new Web3.providers.HttpProvider(RPC_URLS[this.chainId])
    )
    const isV2 = this.version === 2
    const pairContract = new web3.eth.Contract(
      (isV2 ? IPairV2 : IPair) as any,
      this.liquidityToken.address
    )
    const fee = await pairContract.methods.fee().call()
    const precision = isV2
      ? await pairContract.methods.PRECISION().call()
      : 10000
    return (Number(fee) * 100) / precision
  }

  public getOutputAmount(inputAmount: TokenAmount): [TokenAmount, Pair] {
    invariant(this.involvesToken(inputAmount.token), 'TOKEN')
    if (
      JSBI.equal(this.reserve0.raw, ZERO) ||
      JSBI.equal(this.reserve1.raw, ZERO)
    ) {
      throw new InsufficientReservesError()
    }
    const inputReserve = this.reserveOf(inputAmount.token)
    const outputReserve = this.reserveOf(
      inputAmount.token.equals(this.token0) ? this.token1 : this.token0
    )
    const inputAmountWithFee = JSBI.multiply(inputAmount.raw, _997)
    const numerator = JSBI.multiply(inputAmountWithFee, outputReserve.raw)
    const denominator = JSBI.add(
      JSBI.multiply(inputReserve.raw, _1000),
      inputAmountWithFee
    )
    const outputAmount = new TokenAmount(
      inputAmount.token.equals(this.token0) ? this.token1 : this.token0,
      JSBI.divide(numerator, denominator)
    )
    if (JSBI.equal(outputAmount.raw, ZERO)) {
      throw new InsufficientInputAmountError()
    }
    return [
      outputAmount,
      new Pair(
        inputReserve.add(inputAmount),
        outputReserve.subtract(outputAmount),
        this.version
      )
    ]
  }

  public async getOutputAmountAsync(
    inputAmount: TokenAmount,
    pairs: Pair[],
    path: Token[],
    chainId: ChainId,
    account: string
  ): Promise<[TokenAmount, Pair, string[], number, number]> {
    const version = pairs[0].version
    const web3 = new Web3(new Web3.providers.HttpProvider(RPC_URLS[chainId]))

    invariant(this.involvesToken(inputAmount.token), 'TOKEN')
    if (
      JSBI.equal(this.reserve0.raw, ZERO) ||
      JSBI.equal(this.reserve1.raw, ZERO)
    ) {
      throw new InsufficientReservesError()
    }

    const inputReserve = this.reserveOf(inputAmount.token)
    const outputReserve = this.reserveOf(
      inputAmount.token.equals(this.token0) ? this.token1 : this.token0
    )

    let priceUpdate: string[] = []
    let updateFee = 0
    let amountOuts: BigintIsh[]

    if (isContractWithPrice(chainId, version)) {
      const routerContractWithPrice = new web3.eth.Contract(
        IRouterWithPrice as any,
        ROUTER_ADDRESS_WITH_PRICE[chainId]
      )
      ;[priceUpdate, updateFee] = await getFeedPriceAndFee(pairs, chainId)

      amountOuts = await routerContractWithPrice.methods
        .getAmountsOutWithPrice(
          inputAmount.raw.toString(),
          path.map((token) => token.address),
          priceUpdate
        )
        .call({ value: updateFee, from: account })
    } else {
      const routerContract = new web3.eth.Contract(
        IRouter as any,
        getRouterAddress(chainId, version)
      )
      if (this.version === 2) {
        const updateData = await solidityPack(
          path.map((token) => token.address),
          chainId
        )
        amountOuts = await routerContract.methods
          .getAmountsOut(
            inputAmount.raw.toString(),
            path.map((token) => token.address),
            updateData
          )
          .call({ from: account })
      } else {
        amountOuts = await routerContract.methods
          .getAmountsOut(
            inputAmount.raw.toString(),
            path.map((token) => token.address)
          )
          .call()
      }
    }

    const outputAmount = new TokenAmount(
      outputReserve.token,
      amountOuts[amountOuts.length - 1]
    )
    if (JSBI.equal(outputAmount.raw, ZERO)) {
      throw new InsufficientInputAmountError()
    }

    const ratio = outputAmount
      .divide(outputReserve.subtract(outputAmount))
      .toSignificant(6)
    const priceImpactK = 0.001 * Number(ratio) * 100

    return [
      outputAmount,
      new Pair(
        inputReserve.add(inputAmount),
        outputReserve.subtract(outputAmount),
        version
      ),
      priceUpdate,
      updateFee,
      priceImpactK
    ]
  }

  public getInputAmount(outputAmount: TokenAmount): [TokenAmount, Pair] {
    invariant(this.involvesToken(outputAmount.token), 'TOKEN')
    if (
      JSBI.equal(this.reserve0.raw, ZERO) ||
      JSBI.equal(this.reserve1.raw, ZERO) ||
      JSBI.greaterThanOrEqual(
        outputAmount.raw,
        this.reserveOf(outputAmount.token).raw
      )
    ) {
      throw new InsufficientReservesError()
    }

    const outputReserve = this.reserveOf(outputAmount.token)
    const inputReserve = this.reserveOf(
      outputAmount.token.equals(this.token0) ? this.token1 : this.token0
    )
    const numerator = JSBI.multiply(
      JSBI.multiply(inputReserve.raw, outputAmount.raw),
      _1000
    )
    const denominator = JSBI.multiply(
      JSBI.subtract(outputReserve.raw, outputAmount.raw),
      _997
    )
    const inputAmount = new TokenAmount(
      outputAmount.token.equals(this.token0) ? this.token1 : this.token0,
      JSBI.add(JSBI.divide(numerator, denominator), ONE)
    )
    return [
      inputAmount,
      new Pair(
        inputReserve.add(inputAmount),
        outputReserve.subtract(outputAmount),
        this.version
      )
    ]
  }

  public async getInputAmountAsync(
    outputAmount: TokenAmount,
    pairs: Pair[],
    path: Token[],
    chainId: ChainId,
    account: string
  ): Promise<[TokenAmount, Pair, string[], number, number]> {
    const version = pairs[0].version
    const web3 = new Web3(new Web3.providers.HttpProvider(RPC_URLS[chainId]))

    invariant(this.involvesToken(outputAmount.token), 'TOKEN')
    if (
      JSBI.equal(this.reserve0.raw, ZERO) ||
      JSBI.equal(this.reserve1.raw, ZERO) ||
      JSBI.greaterThanOrEqual(
        outputAmount.raw,
        this.reserveOf(outputAmount.token).raw
      )
    ) {
      throw new InsufficientReservesError()
    }

    const outputReserve = this.reserveOf(outputAmount.token)
    const inputReserve = this.reserveOf(
      outputAmount.token.equals(this.token0) ? this.token1 : this.token0
    )

    let priceUpdate: string[] = []
    let updateFee = 0
    let amountIns: BigintIsh[]

    if (isContractWithPrice(chainId, version)) {
      const routerContractWithPrice = new web3.eth.Contract(
        IRouterWithPrice as any,
        ROUTER_ADDRESS_WITH_PRICE[chainId]
      )
      ;[priceUpdate, updateFee] = await getFeedPriceAndFee(pairs, chainId)

      amountIns = await routerContractWithPrice.methods
        .getAmountsInWithPrice(
          outputAmount.raw.toString(),
          path.map((token) => token.address),
          priceUpdate
        )
        .call({ value: updateFee, from: account })
    } else {
      const routerContract = new web3.eth.Contract(
        IRouter as any,
        getRouterAddress(chainId, version)
      )
      if (this.version === 2) {
        const updateData = await solidityPack(
          path.map((token) => token.address),
          chainId
        )
        amountIns = await routerContract.methods
          .getAmountsIn(
            outputAmount.raw.toString(),
            path.map((token) => token.address),
            updateData
          )
          .call({ from: account })
      } else {
        amountIns = await routerContract.methods
          .getAmountsIn(
            outputAmount.raw.toString(),
            path.map((token) => token.address)
          )
          .call()
      }
    }

    const inputAmount = new TokenAmount(inputReserve.token, amountIns[0])

    const ratio = outputAmount
      .divide(outputReserve.subtract(outputAmount))
      .toSignificant(6)
    const priceImpactK = 0.001 * Number(ratio) * 100

    return [
      inputAmount,
      new Pair(
        inputReserve.add(inputAmount),
        outputReserve.subtract(outputAmount),
        version
      ),
      priceUpdate,
      updateFee,
      priceImpactK
    ]
  }

  public getLiquidityMinted(
    totalSupply: TokenAmount,
    tokenAmountA: TokenAmount,
    tokenAmountB: TokenAmount
  ): TokenAmount {
    invariant(totalSupply.token.equals(this.liquidityToken), 'LIQUIDITY')
    const tokenAmounts = tokenAmountA.token.sortsBefore(tokenAmountB.token) // does safety checks
      ? [tokenAmountA, tokenAmountB]
      : [tokenAmountB, tokenAmountA]
    invariant(
      tokenAmounts[0].token.equals(this.token0) &&
        tokenAmounts[1].token.equals(this.token1),
      'TOKEN'
    )

    let liquidity: JSBI
    if (JSBI.equal(totalSupply.raw, ZERO)) {
      liquidity = JSBI.subtract(
        sqrt(JSBI.multiply(tokenAmounts[0].raw, tokenAmounts[1].raw)),
        MINIMUM_LIQUIDITY
      )
    } else {
      const amount0 = JSBI.divide(
        JSBI.multiply(tokenAmounts[0].raw, totalSupply.raw),
        this.reserve0.raw
      )
      const amount1 = JSBI.divide(
        JSBI.multiply(tokenAmounts[1].raw, totalSupply.raw),
        this.reserve1.raw
      )
      liquidity = JSBI.lessThanOrEqual(amount0, amount1) ? amount0 : amount1
    }
    if (!JSBI.greaterThan(liquidity, ZERO)) {
      throw new InsufficientInputAmountError()
    }
    return new TokenAmount(this.liquidityToken, liquidity)
  }

  public getLiquidityValue(
    token: Token,
    totalSupply: TokenAmount,
    liquidity: TokenAmount,
    feeOn: boolean = false,
    kLast?: BigintIsh
  ): TokenAmount {
    invariant(this.involvesToken(token), 'TOKEN')
    invariant(totalSupply.token.equals(this.liquidityToken), 'TOTAL_SUPPLY')
    invariant(liquidity.token.equals(this.liquidityToken), 'LIQUIDITY')
    invariant(JSBI.lessThanOrEqual(liquidity.raw, totalSupply.raw), 'LIQUIDITY')

    let totalSupplyAdjusted: TokenAmount
    if (!feeOn) {
      totalSupplyAdjusted = totalSupply
    } else {
      invariant(!!kLast, 'K_LAST')
      const kLastParsed = parseBigintIsh(kLast)
      if (!JSBI.equal(kLastParsed, ZERO)) {
        const rootK = sqrt(JSBI.multiply(this.reserve0.raw, this.reserve1.raw))
        const rootKLast = sqrt(kLastParsed)
        if (JSBI.greaterThan(rootK, rootKLast)) {
          const numerator = JSBI.multiply(
            totalSupply.raw,
            JSBI.subtract(rootK, rootKLast)
          )
          const denominator = JSBI.add(JSBI.multiply(rootK, FIVE), rootKLast)
          const feeLiquidity = JSBI.divide(numerator, denominator)
          totalSupplyAdjusted = totalSupply.add(
            new TokenAmount(this.liquidityToken, feeLiquidity)
          )
        } else {
          totalSupplyAdjusted = totalSupply
        }
      } else {
        totalSupplyAdjusted = totalSupply
      }
    }

    return new TokenAmount(
      token,
      JSBI.divide(
        JSBI.multiply(liquidity.raw, this.reserveOf(token).raw),
        totalSupplyAdjusted.raw
      )
    )
  }
}
